/* import __COLOCATED_TEMPLATE__ from './phone-config.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type PhoneConfig from 'embercom/models/operator/visual-builder/step/attribute-collector/phone-config';

interface Signature {
  Args: {
    config: PhoneConfig;
    isReadOnly: boolean;
    attributeDataType?: string;
  };
  Element: HTMLDivElement;
}

const DEFAULT_FINISH_KEY = '#';
// Numeric types: only DTMF digits make sense (speech transcript can't be stored as a number)
const NUMERIC_TYPES = ['integer', 'float'];
// Text types: only speech makes sense (you can't key in a name or phrase on a dialpad)
const TEXT_TYPES = ['string'];

export default class AttributeCollectorPhoneConfig extends Component<Signature> {
  @service declare intl: IntlService;

  readonly finishOnKeyOptions = [
    { text: '#', value: '#' },
    { text: '*', value: '*' },
  ];

  get speechDisabledForType() {
    return NUMERIC_TYPES.includes(this.args.attributeDataType ?? '');
  }

  get digitsDisabledForType() {
    return TEXT_TYPES.includes(this.args.attributeDataType ?? '');
  }

  get inputModeItems() {
    return [
      {
        text: this.intl.t(
          'operator.workflows.visual-builder.attribute-collector.phone-config.mode-digits-fixed',
        ),
        value: 'digits_fixed',
        isDisabled: this.digitsDisabledForType,
      },
      {
        text: this.intl.t(
          'operator.workflows.visual-builder.attribute-collector.phone-config.mode-digits-variable',
        ),
        value: 'digits_variable',
        isDisabled: this.digitsDisabledForType,
      },
      {
        text: this.intl.t(
          'operator.workflows.visual-builder.attribute-collector.phone-config.mode-speech',
        ),
        value: 'speech',
        isDisabled: this.speechDisabledForType,
      },
    ];
  }

  get selectedInputModeValue(): string | undefined {
    return this.args.config.inputMode || undefined;
  }

  get selectedInputModeLabel() {
    let match = this.inputModeItems.find((item) => item.value === this.args.config.inputMode);
    return (
      match?.text ||
      this.intl.t(
        'operator.workflows.visual-builder.attribute-collector.phone-config.select-input-mode',
      )
    );
  }

  @action
  onInputModeSelect(value: string) {
    this.args.config.inputMode = value;

    if (value === 'digits_fixed') {
      if (!this.args.config.numDigits || this.args.config.numDigits < 1) {
        this.args.config.numDigits = 4;
      }
      this.args.config.finishOnKey = '';
    } else if (value === 'digits_variable') {
      this.args.config.numDigits = 0;
      this.args.config.finishOnKey = this.args.config.finishOnKey || DEFAULT_FINISH_KEY;
    } else if (value === 'speech') {
      this.args.config.numDigits = 0;
      this.args.config.finishOnKey = '';
    }
  }

  @action
  updateNumDigits(event: Event) {
    let value = (event.target as HTMLInputElement).value;
    let parsed = parseInt(value, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 20) {
      this.args.config.numDigits = parsed;
    }
  }

  @action
  onFinishOnKeySelect(event: Event) {
    this.args.config.finishOnKey = (event.target as HTMLSelectElement).value;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::AttributeCollector::PhoneConfig': typeof AttributeCollectorPhoneConfig;
    'workflows/graph-editor/node-items/steps/attribute-collector/phone-config': typeof AttributeCollectorPhoneConfig;
  }
}
